import { useCallback, useEffect, useRef, useState } from "react";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createLinkNode,
  $isLinkNode,
  TOGGLE_LINK_COMMAND,
} from "@lexical/link";
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  type RangeSelection,
} from "lexical";
import {
  FloatingLinkEditor,
  LinkEditPopover,
  OPEN_LINK_DIALOG_COMMAND,
  isValidLinkUrl,
  sanitizeUrl,
} from "@tgpe/editor-core";

import { useTelegramWebApp } from "../../hooks/useTelegramWebApp.js";

interface LinkPluginsProps {
  /** Anchor element for the LinkEditPopover (toolbar's link button). */
  anchorRef: React.RefObject<HTMLElement | null>;
}

/**
 * Mini App link editing — mirrors the desktop client's wiring (see
 * packages/client/src/editor/plugins/ToolbarPlugin.tsx).
 *
 * - Toolbar's link button dispatches OPEN_LINK_DIALOG_COMMAND, which
 *   opens a Radix popover anchored to that button. The popover lets the
 *   user enter / edit a URL (and a link text when inserting fresh).
 * - When the cursor lands inside an existing link, FloatingLinkEditor
 *   surfaces a bubble next to the link with Edit / Copy / Open / Unlink
 *   actions.
 *
 * Replaces the previous window.prompt shim, which is unreliable in the
 * Telegram WebView and gave no way to edit or open existing links.
 */
export function LinkInsertPlugin({ anchorRef }: LinkPluginsProps) {
  const [editor] = useLexicalComposerContext();
  const wa = useTelegramWebApp();

  const [open, setOpen] = useState(false);
  const [initialUrl, setInitialUrl] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [showLinkTextField, setShowLinkTextField] = useState(false);
  const savedSelectionRef = useRef<RangeSelection | null>(null);

  useEffect(() => {
    return editor.registerCommand(
      OPEN_LINK_DIALOG_COMMAND,
      () => {
        editor.getEditorState().read(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return;
          savedSelectionRef.current = selection.clone();

          const anchorNode = selection.anchor.getNode();
          let existingUrl = "";
          let current = anchorNode.getParent();
          while (current !== null) {
            if ($isLinkNode(current)) {
              existingUrl = current.getURL();
              break;
            }
            current = current.getParent();
          }

          const selectedText = selection.getTextContent().trim();
          if (!existingUrl && selectedText && isValidLinkUrl(selectedText)) {
            setInitialUrl(sanitizeUrl(selectedText));
            setIsEditing(false);
            setShowLinkTextField(true);
          } else {
            setInitialUrl(existingUrl);
            setIsEditing(existingUrl !== "");
            setShowLinkTextField(false);
          }
          setOpen(true);
        });
        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor]);

  const handleSubmit = useCallback(
    (url: string, linkText?: string) => {
      setOpen(false);
      editor.focus(() => {
        editor.update(() => {
          if (savedSelectionRef.current) {
            $setSelection(savedSelectionRef.current);
          }
          if (linkText) {
            const linkNode = $createLinkNode(url);
            linkNode.append($createTextNode(linkText));
            const sel = $getSelection();
            if ($isRangeSelection(sel)) {
              sel.insertNodes([linkNode]);
            }
          } else {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
          }
          savedSelectionRef.current = null;
        });
      });
    },
    [editor],
  );

  const handleRemove = useCallback(() => {
    setOpen(false);
    editor.focus(() => {
      editor.update(() => {
        if (savedSelectionRef.current) {
          $setSelection(savedSelectionRef.current);
        }
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
        savedSelectionRef.current = null;
      });
    });
  }, [editor]);

  const handleFloatingEdit = useCallback(
    (url: string) => {
      editor.getEditorState().read(() => {
        const sel = $getSelection();
        if ($isRangeSelection(sel)) {
          savedSelectionRef.current = sel.clone();
        }
      });
      setInitialUrl(url);
      setIsEditing(true);
      setShowLinkTextField(false);
      setOpen(true);
    },
    [editor],
  );

  const handleFloatingUnlink = useCallback(() => {
    editor.update(() => {
      const sel = $getSelection();
      if ($isRangeSelection(sel)) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      }
    });
  }, [editor]);

  const handleOpenLink = useCallback(
    (url: string) => {
      if (wa?.openLink) wa.openLink(url);
      else window.open(url, "_blank", "noopener,noreferrer");
    },
    [wa],
  );

  return (
    <>
      <LinkEditPopover
        open={open}
        onOpenChange={setOpen}
        onSubmit={handleSubmit}
        onRemove={handleRemove}
        initialUrl={initialUrl}
        isEditing={isEditing}
        showLinkTextField={showLinkTextField}
        anchorRef={anchorRef}
      />
      <FloatingLinkEditor
        onEdit={handleFloatingEdit}
        onUnlink={handleFloatingUnlink}
        onOpenLink={handleOpenLink}
        size="comfortable"
        hidden={open}
      />
    </>
  );
}
